import { useRolodexFileLS } from 'features/rolodex'
import { useMemo } from 'react'

/**
 * Hook to fetch all weld scan file names from phased array inspections
 *
 * Returns the folder names under the `weld/` path, where each folder
 * represents a single weld scan file (regardless of whether it has indications).
 *
 * @param inspectionId - The phased array inspection ID
 * @returns Array of weld scan file names
 */
export const useAllWeldScanFiles = (
  inspectionId: string | undefined
): {
  scanFileNames: string[]
  isLoading: boolean
} => {
  const { fileRefs, isLoading } = useRolodexFileLS(
    inspectionId
      ? {
          entry_id: inspectionId,
          path: 'weld/**',
        }
      : undefined
  )

  const scanFileNames = useMemo(() => {
    if (!fileRefs || fileRefs.length === 0) {
      return []
    }

    // Extract unique folder names from paths like "weld/filename/..."
    const folderNames = new Set<string>()

    fileRefs.forEach((ref) => {
      // Remove leading 'weld/' and get the first segment
      const pathWithoutPrefix = ref.path.replace(/^weld\//, '')
      const firstSegment = pathWithoutPrefix.split('/')[0]

      if (firstSegment && firstSegment !== '') {
        folderNames.add(firstSegment)
      }
    })

    const result = Array.from(folderNames).sort()
    return result
  }, [fileRefs])

  return {
    scanFileNames,
    isLoading,
  }
}
